/**
 * Sketchy arrowhead marker for hand-drawn edges.
 *
 * One SVG <marker> definition shared by every edge in the diagram (rendered
 * by DiagramRenderer only in hand-drawn mode). ElkEdge references it via
 * SKETCH_ARROW_MARKER_URL instead of React Flow's built-in ArrowClosed
 * marker. Marker geometry mirrors the built-in one (same viewBox, same
 * triangle points, markerUnits="strokeWidth" so it scales with the edge
 * stroke width, markerWidth/Height 16 matching elk-to-flow.ts) but the
 * triangle is rough-generated with a fixed seed.
 *
 * Color: a single fixed color reproduces today's behavior exactly — every
 * arrowhead currently renders in React Flow's defaultMarkerColor (#b1b1b7)
 * because elk-to-flow never sets markerEnd.color, and EdgeStyle.stroke does
 * not recolor markers either. Per-edge marker colors would need per-color
 * marker defs (`context-stroke` is poorly supported) and are not needed.
 */
import rough from "roughjs";
import type { SketchPath } from "./node-paths";

export const SKETCH_ARROW_MARKER_ID = "diagram-view-sketch-arrow";

/** marker-end value referencing the shared sketchy arrowhead. */
export const SKETCH_ARROW_MARKER_URL = `url('#${SKETCH_ARROW_MARKER_ID}')`;

// React Flow's defaultMarkerColor — see the rationale in the module comment.
const ARROW_COLOR = "#b1b1b7";

// Fixed seed: all arrowheads intentionally share one wobble (one marker def
// serves the whole diagram), it only has to be deterministic.
const ARROW_SEED = 7;

// Lower roughness than nodes/edges (1.5): the marker lives in a 20x20-unit
// viewBox, where full-strength wobble would deform the tiny triangle.
const ARROW_ROUGHNESS = 1;

// Module-level constant: the marker never varies, so generate it once.
const ARROW_PATHS: readonly SketchPath[] = (() => {
  const generator = rough.generator();
  // Same triangle as React Flow's ArrowClosed symbol, solid-filled.
  const drawable = generator.polygon(
    [
      [-5, -4],
      [0, 0],
      [-5, 4],
    ],
    {
      seed: ARROW_SEED,
      roughness: ARROW_ROUGHNESS,
      stroke: ARROW_COLOR,
      strokeWidth: 1,
      fill: ARROW_COLOR,
      fillStyle: "solid",
    },
  );
  return generator.toPaths(drawable).map((p) => ({
    d: p.d,
    stroke: p.stroke,
    strokeWidth: p.strokeWidth,
    fill: p.fill ?? "none",
  }));
})();

/**
 * Defs-only SVG hosting the shared sketchy arrowhead marker. Mounted once
 * per diagram by DiagramRenderer when the appearance theme is "hand-drawn".
 * Cross-<svg> marker references work within the same document — React
 * Flow's own marker defs rely on the same mechanism.
 */
export function SketchArrowMarkerDefs() {
  return (
    <svg
      aria-hidden="true"
      data-testid="sketch-arrow-defs"
      // Defs-only content paints nothing, but without explicit zero sizing
      // the svg would take the default 300x150 layout box.
      style={{ position: "absolute", width: 0, height: 0 }}
    >
      <defs>
        <marker
          id={SKETCH_ARROW_MARKER_ID}
          markerWidth="16"
          markerHeight="16"
          viewBox="-10 -10 20 20"
          markerUnits="strokeWidth"
          orient="auto-start-reverse"
          refX="0"
          refY="0"
        >
          {ARROW_PATHS.map((p, i) => (
            <path
              // Paths are a module-level constant; index keys are stable.
              key={i}
              d={p.d}
              stroke={p.stroke}
              strokeWidth={p.strokeWidth}
              fill={p.fill}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </marker>
      </defs>
    </svg>
  );
}
